import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ProcessingAnalytics {
  totalProcessed: number;
  successRate: number;
  averageProcessingTime: number;
  averageConfidence: number;
  errorBreakdown: Array<{
    error_type: string;
    count: number;
    percentage: number;
  }>;
  dailyVolume: Array<{
    date: string;
    processed: number;
    succeeded: number;
    failed: number;
  }>;
  confidenceDistribution: Array<{
    range: string;
    count: number;
    percentage: number;
  }>;
  matchingCategoryStats: Array<{
    category: string;
    count: number;
    successRate: number;
    avgConfidence: number;
  }>;
  processingTrends: Array<{
    hour: number;
    averageTime: number;
    throughput: number;
  }>;
}

export const useProcessingAnalytics = () => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date().toISOString()
  });

  const analyticsQuery = useQuery({
    queryKey: ['processing-analytics', user?.id, dateRange],
    queryFn: async (): Promise<ProcessingAnalytics> => {
      if (!user?.id) throw new Error('User not authenticated');

      // Fetch bills within date range
      const { data: bills, error: billsError } = await supabase
        .from('bills')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end);

      if (billsError) throw billsError;

      // Fetch queue items for additional insights
      const { data: queueItems, error: queueError } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end);

      if (queueError) throw queueError;

      // Fetch analytics events
      const { data: events, error: eventsError } = await supabase
        .from('analytics_events')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end)
        .in('event_type', ['bill_processed', 'bill_created_from_queue', 'bill_creation_failed']);

      if (eventsError) throw eventsError;

      // Calculate analytics
      const totalProcessed = (bills?.length || 0) + (queueItems?.length || 0);
      const successfulBills = bills?.filter(b => b.processing_status === 'completed') || [];
      const errorBills = bills?.filter(b => b.processing_status === 'failed') || [];
      const successRate = totalProcessed > 0 ? (successfulBills.length / totalProcessed) * 100 : 0;

      // Average processing time
      const processingTimes = bills?.map(bill => {
        if (bill.processing_started_at && bill.processing_completed_at) {
          return new Date(bill.processing_completed_at).getTime() - new Date(bill.processing_started_at).getTime();
        }
        return null;
      }).filter(Boolean) || [];

      const averageProcessingTime = processingTimes.length > 0 
        ? processingTimes.reduce((sum, time) => sum + time, 0) / processingTimes.length / 1000
        : 0;

      // Average confidence
      const confidenceScores = [...(bills || []), ...(queueItems || [])]
        .map(item => item.ai_confidence)
        .filter(c => c !== null && c !== undefined);

      const averageConfidence = confidenceScores.length > 0
        ? (confidenceScores.reduce((sum, score) => sum + score, 0) / confidenceScores.length) * 100
        : 0;

      // Error breakdown
      const errorBreakdown = errorBills.reduce((acc: any[], bill) => {
        const errorType = bill.processing_error || 'Unknown Error';
        const existing = acc.find(e => e.error_type === errorType);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ error_type: errorType, count: 1, percentage: 0 });
        }
        return acc;
      }, []).map(error => ({
        ...error,
        percentage: (error.count / errorBills.length) * 100
      }));

      // Daily volume
      const dailyVolume = [];
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const dayBills = bills?.filter(b => b.created_at.startsWith(dateStr)) || [];
        const dayQueue = queueItems?.filter(q => q.created_at.startsWith(dateStr)) || [];
        
        dailyVolume.push({
          date: dateStr,
          processed: dayBills.length + dayQueue.length,
          succeeded: dayBills.filter(b => b.processing_status === 'completed').length + 
                    dayQueue.filter(q => q.processing_status === 'completed').length,
          failed: dayBills.filter(b => b.processing_status === 'failed').length +
                 dayQueue.filter(q => q.processing_status === 'failed').length
        });
      }

      // Confidence distribution
      const confidenceRanges = [
        { min: 0, max: 0.3, range: '0-30%' },
        { min: 0.3, max: 0.5, range: '30-50%' },
        { min: 0.5, max: 0.7, range: '50-70%' },
        { min: 0.7, max: 0.9, range: '70-90%' },
        { min: 0.9, max: 1.0, range: '90-100%' }
      ];

      const confidenceDistribution = confidenceRanges.map(range => {
        const count = confidenceScores.filter(score => score >= range.min && score < range.max).length;
        return {
          range: range.range,
          count,
          percentage: confidenceScores.length > 0 ? (count / confidenceScores.length) * 100 : 0
        };
      });

      // Matching category stats
      const categories = ['NEW-NEW', 'OLD-NEW', 'OLD-OLD'];
      const matchingCategoryStats = categories.map(category => {
        const categoryItems = queueItems?.filter(q => q.matching_category === category) || [];
        const successfulItems = categoryItems.filter(q => q.processing_status === 'completed');
        const confidences = categoryItems.map(q => q.ai_confidence).filter(Boolean);
        
        return {
          category,
          count: categoryItems.length,
          successRate: categoryItems.length > 0 ? (successfulItems.length / categoryItems.length) * 100 : 0,
          avgConfidence: confidences.length > 0 ? (confidences.reduce((sum, c) => sum + c, 0) / confidences.length) * 100 : 0
        };
      });

      // Processing trends by hour
      const processingTrends = Array.from({ length: 24 }, (_, hour) => {
        const hourBills = bills?.filter(bill => {
          if (!bill.processing_started_at) return false;
          return new Date(bill.processing_started_at).getHours() === hour;
        }) || [];

        const hourProcessingTimes = hourBills.map(bill => {
          if (bill.processing_started_at && bill.processing_completed_at) {
            return new Date(bill.processing_completed_at).getTime() - new Date(bill.processing_started_at).getTime();
          }
          return null;
        }).filter(Boolean);

        return {
          hour,
          averageTime: hourProcessingTimes.length > 0 
            ? hourProcessingTimes.reduce((sum, time) => sum + time, 0) / hourProcessingTimes.length / 1000
            : 0,
          throughput: hourBills.length
        };
      });

      return {
        totalProcessed,
        successRate,
        averageProcessingTime,
        averageConfidence,
        errorBreakdown,
        dailyVolume,
        confidenceDistribution,
        matchingCategoryStats,
        processingTrends
      };
    },
    enabled: !!user?.id,
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  return {
    analytics: analyticsQuery.data,
    isLoading: analyticsQuery.isLoading,
    error: analyticsQuery.error,
    refetch: analyticsQuery.refetch,
    dateRange,
    setDateRange
  };
};